import { TIER_NAMES, type LadderRung, type PlanTier } from "../../lib/api";

export const UPGRADE_ACTION = "See the plan";

/**
 * THE LEAD ON FREE, WHERE WHAT WAS REFUSED IS A DOORMAN TURN (ruling C10) —
 * the paywall's, and the only lead this card is ever given.
 *
 * It used to have a default, "This month's allowance is used. Work pauses
 * until it resets.", and inside the plan card that stacked a second "used"
 * sentence under the card's own "This month's allowance is used up." (owner,
 * 2026-09-23; wave 61). The card says it once; the offer is the rung and its
 * price line and nothing above them.
 *
 * What follows it is the rung's own label and the SERVER'S price string ("Lite
 * is $10 a month"), which is a price and never a capacity (ruling C2).
 */
export const UPGRADE_LEAD_FREE_DOORMAN =
  "Free does no AI work: a plan is what pays for your doorman.";

/**
 * The paywall's ORDER (docs/plans.md §5.4).
 *
 * An owner who has used their limit is offered **the next tier up, priced** —
 * not a way to buy this month twice (the paid reset is deleted, ruling
 * R-0923-3 (1)). The reasoning is in the doc, and it outlived the packs it was
 * written about: a product that answers "you have run out" with "buy another
 * one of the same" teaches people to sit on the smallest plan forever, which
 * is worse for them and worse for us.
 *
 * A PRICE IS ALLOWED HERE; A CAPACITY IS NOT. This is a funding surface, and
 * the auto-renewal rules require the price in visual proximity to the control
 * that agrees to it. What a rung may NOT state is a quantity of AI work (ruling
 * C2), so it is described by its RATIO to the rung below it — "4× Personal" —
 * which discloses nothing about either.
 *
 * ONE TAP, AND THE TAP OPENS THE PLANS PANE. Not because a tap is expensive,
 * but because the tier's consent lives there: one server-authored disclosure,
 * one checkbox, one place that can be got right. A second checkout surface
 * inside a chat card would be a second place to get consent wrong, and consent
 * is the one thing this lane is not allowed to be casual about.
 *
 * THE TAP IS A CALLBACK, AND THERE IS NO ADDRESS BEHIND IT. This action used to
 * be a plain anchor at `/you/plan` — a full page reload of a single-bundle app,
 * from inside a dialog, to reach a pane the shell can already open in place. That
 * address exists for the Stripe return and for the auto-renewal disclosure to
 * name; nothing INSIDE the app navigates to it. So the card asks its caller for
 * the opener, and a caller that has none renders no action at all — which is
 * also what the paywall dialog wants, since its own button is the one call to
 * action on that screen.
 */
export function UpgradeOffer({
  nextTier,
  ladder,
  /**
   * A sentence above the rung, or none. The plan card passes none: its own
   * lead has already said the allowance is used (wave 61).
   */
  lead,
  onSeePlans,
  priceSuffix,
}: {
  nextTier: PlanTier;
  ladder: LadderRung[];
  lead?: string;
  /** Open the plans pane. Absent means this card carries no action. */
  onSeePlans?: (() => void) | undefined;
  /** What the price includes — "incl. VAT" — or null (ruling R-0923-5). */
  priceSuffix?: string | null;
}) {
  const rung = ladder.find((r) => r.tier === nextTier);
  if (!rung) return null;

  return (
    <div
      className="mt-4 rounded-r-lg border-l-2 border-accent/70 bg-accent/[0.04] px-4 py-3"
      data-offer={nextTier}
    >
      {lead && <p className="mb-1 text-sm text-text-soft">{lead}</p>}
      <p className="text-sm text-text-soft">
        {rung.label} is {rung.monthly_display} a month
        {priceSuffix ? ` ${priceSuffix}` : ""}
        {rung.relative_to
          ? ` — ${rung.relative_to.times}× ${TIER_NAMES[rung.relative_to.tier]}.`
          : "."}
      </p>
      {onSeePlans && (
        <p className="mt-3">
          <button
            type="button"
            onClick={onSeePlans}
            className="inline-flex h-9 items-center rounded-lg border border-border-strong px-4
                       text-sm font-medium text-text transition-colors duration-75
                       hover:border-border-loud hover:bg-surface focus-visible:outline-none
                       focus-visible:ring-2 focus-visible:ring-focus/70"
          >
            {UPGRADE_ACTION}
          </button>
        </p>
      )}
    </div>
  );
}
